import React, { useMemo, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import SubscriptionPaymentService from "../../services/SubscriptionPaymentService";
import UploadAndDownloadService from "../../services/uploadAndDownloadFile";

/*
 * Client side of the subscription payment: 1) amount and mode, 2) proof, 3) summary and
 * sending. The payment is then waiting for the validation of the super admin.
 */
const VAT_RATE = 19;
const MODES = [
  { value: "VIREMENT", label: "Virement bancaire" },
  { value: "CHEQUE", label: "Chèque" },
  { value: "ESPECES", label: "Espèces" },
  { value: "MOBILE", label: "Paiement mobile" },
];
const MAX_PROOF_BYTES = 5 * 1024 * 1024;
const PROOF_TYPES = ["application/pdf", "image/png", "image/jpeg"];

const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const formatIso = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// same rule as the server: the renewed period starts the day after the end of the contract
// (or today when the contract is already over) and lasts one month or one year
const nextPeriod = (contract) => {
  const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  let start = today;
  if (contract.endDate) {
    const afterEnd = fromIso(contract.endDate);
    afterEnd.setDate(afterEnd.getDate() + 1);
    if (afterEnd > today) start = afterEnd;
  }
  const end = new Date(start);
  if (contract.periodicity === "ANNUEL") end.setFullYear(end.getFullYear() + 1);
  else end.setMonth(end.getMonth() + 1);
  end.setDate(end.getDate() - 1);
  return { start: toIso(start), end: toIso(end) };
};

const ERRORS = {
  forbidden: "Action non autorisée",
  no_company: "Aucune société n'est associée à votre compte",
  no_contract: "Aucun contrat à renouveler",
  pending_exists: "Un paiement est déjà en attente de validation",
};

const SubscriptionPaymentDialog = ({ contract, onClose, onSent }) => {
  const period = useMemo(() => nextPeriod(contract), [contract]);
  const defaultAmount = useMemo(
    () => (Number(contract.amount) * (1 + VAT_RATE / 100)).toFixed(3),
    [contract]
  );
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(defaultAmount);
  const [mode, setMode] = useState("VIREMENT");
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState(null);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState("");

  const needsProof = mode !== "ESPECES";

  const validateStep = (target) => {
    const next = {};
    if (target === 1) {
      if (!amount || Number(amount) <= 0) next.amount = "Le montant doit être supérieur à 0";
      if (mode !== "ESPECES" && reference.trim().length < 3) next.reference = "La référence du paiement est obligatoire";
    }
    if (target === 2 && needsProof && !proof) next.proof = "Ajoutez le justificatif du paiement";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = () => {
    if (validateStep(step)) setStep(step + 1);
  };

  const onProofChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!PROOF_TYPES.includes(file.type)) {
      setErrors({ proof: "Format accepté : PDF, PNG ou JPEG" });
      return;
    }
    if (file.size > MAX_PROOF_BYTES) {
      setErrors({ proof: "Le justificatif ne doit pas dépasser 5 Mo" });
      return;
    }
    setErrors({});
    setProof(file);
  };

  const send = async () => {
    if (sending) return;
    setSending(true);
    setServerError("");
    try {
      let proofFileUrl = null;
      if (proof) {
        const data = new FormData();
        data.append("picture", proof);
        const upload = await UploadAndDownloadService.uploadFile("/retail/uploadFile/uploadFile/SubscriptionProof", data);
        proofFileUrl = upload.data && upload.data.success;
        if (!proofFileUrl) {
          setServerError("L'envoi du justificatif a échoué. Réessayez.");
          setSending(false);
          return;
        }
      }
      const response = await SubscriptionPaymentService.submit({
        amount: Number(amount),
        mode,
        reference: reference.trim(),
        proofFileUrl,
      });
      if (response.data && response.data.success) {
        onSent();
        onClose();
        return;
      }
      const fieldErrors = response.data && response.data.fieldErrors;
      setServerError(
        (fieldErrors && Object.values(fieldErrors)[0]) ||
          ERRORS[response.data && response.data.error] ||
          "Le paiement n'a pas pu être envoyé"
      );
    } catch (e) {
      setServerError("Erreur : il y a un problème de connexion");
    }
    setSending(false);
  };

  const steps = ["Montant et mode", "Justificatif", "Envoi"];

  return (
    <Dialog open onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Payer mon abonnement</DialogTitle>
      <DialogContent>
        <div className="d-flex align-items-center mb-3" style={{ gap: 8, fontSize: 13 }}>
          {steps.map((label, index) => (
            <React.Fragment key={label}>
              <span
                className={"badge " + (step === index + 1 ? "badge-dark" : "badge-light")}
                style={{ borderRadius: 11, padding: "5px 8px" }}
              >
                {index + 1}
              </span>
              <span className={step === index + 1 ? "" : "text-muted"}>{label}</span>
              {index < steps.length - 1 && <span style={{ width: 20, borderTop: "1px solid #ccc" }} />}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <>
            <div className="form-group">
              <label>Période à payer</label>
              <input className="form-control" readOnly value={`${formatIso(period.start)} au ${formatIso(period.end)}`} />
            </div>
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="payAmount">Montant payé TTC (TND)</label>
                  <input
                    id="payAmount"
                    type="number"
                    min="0"
                    step="0.001"
                    className={"form-control" + (errors.amount ? " is-invalid" : "")}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <div className="invalid-feedback">{errors.amount}</div>
                  <small className="text-muted">Montant HT {Number(contract.amount).toFixed(3)} + TVA {VAT_RATE} %</small>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="payMode">Mode de paiement</label>
                  <select id="payMode" className="form-control" value={mode} onChange={(e) => setMode(e.target.value)}>
                    {MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {mode !== "ESPECES" && (
              <div className="form-group">
                <label htmlFor="payRef">{mode === "CHEQUE" ? "Numéro du chèque" : "Référence de l'opération"}</label>
                <input
                  id="payRef"
                  className={"form-control" + (errors.reference ? " is-invalid" : "")}
                  value={reference}
                  maxLength={100}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Ex : 8841"
                />
                <div className="invalid-feedback">{errors.reference}</div>
              </div>
            )}
          </>
        )}

        {step === 2 && (
          <div className="form-group">
            <label htmlFor="payProof">Justificatif {needsProof ? "" : "(facultatif)"}</label>
            <input
              id="payProof"
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              className={"form-control-file" + (errors.proof ? " is-invalid" : "")}
              onChange={onProofChange}
            />
            {errors.proof && <div className="text-danger small mt-1">{errors.proof}</div>}
            {proof && <div className="small mt-2">Fichier : {proof.name}</div>}
            <small className="text-muted d-block mt-2">PDF, PNG ou JPEG, 5 Mo maximum.</small>
          </div>
        )}

        {step === 3 && (
          <>
            <table className="table table-sm mb-2">
              <tbody>
                <tr>
                  <td className="text-muted">Période</td>
                  <td>
                    {formatIso(period.start)} au {formatIso(period.end)}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Montant TTC</td>
                  <td>{Number(amount).toFixed(3)} TND</td>
                </tr>
                <tr>
                  <td className="text-muted">Mode</td>
                  <td>{MODES.find((m) => m.value === mode).label}</td>
                </tr>
                {reference && (
                  <tr>
                    <td className="text-muted">Référence</td>
                    <td>{reference}</td>
                  </tr>
                )}
                <tr>
                  <td className="text-muted">Justificatif</td>
                  <td>{proof ? proof.name : "-"}</td>
                </tr>
              </tbody>
            </table>
            <small className="text-muted">
              Après l'envoi, votre paiement est vérifié par IWARE. Votre abonnement est prolongé dès la validation.
            </small>
            {serverError && <div className="alert alert-danger mt-3 mb-0">{serverError}</div>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <button className="btn btn-outline-secondary" onClick={onClose} disabled={sending}>
          Annuler
        </button>
        {step > 1 && (
          <button className="btn btn-outline-dark" onClick={() => setStep(step - 1)} disabled={sending}>
            Retour
          </button>
        )}
        {step < 3 ? (
          <button className="btn btn-dark" onClick={goNext}>
            Suivant
          </button>
        ) : (
          <button className="btn btn-dark" onClick={send} disabled={sending}>
            <i className="fas fa-paper-plane mr-1"></i>Envoyer le paiement
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionPaymentDialog;
